import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Collapse,
    TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';

const RecentExtractionJobs = ({ textbookId, textbooks, onViewResults, onError }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 20 });
            if (textbookId) params.set('textbook_id', textbookId);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extraction-jobs?${params.toString()}`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            setJobs(response.data.jobs);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Fetch jobs error:', error);
            onError(error.response?.data?.error || 'Failed to fetch extraction jobs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, [page]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'processing':
                return 'info';
            case 'pending':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getTextbookName = (tbId) => {
        const textbook = textbooks.find((tb) => tb.id === tbId);
        return textbook ? (textbook.title || 'Unknown Textbook') : `Textbook ${tbId}`;
    };

    const handleViewResults = async (jobId) => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extraction-jobs/${jobId}`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            if (response.data.extracted_questions && response.data.extracted_questions.length > 0) {
                const questions = response.data.extracted_questions;
                const firstPage = questions[0]?.page || null;
                onViewResults(questions, jobId, firstPage);
            } else {
                onError('No questions found in this extraction job');
            }
        } catch (error) {
            console.error('View results error:', error);
            onError(error.response?.data?.error || 'Failed to load extraction results');
        }
    };

    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to delete this extraction job?')) {
            return;
        }

        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extraction-jobs/${jobId}`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            // If this was the last item on the current page, go back a page
            if (jobs.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                fetchJobs();
            }
        } catch (error) {
            console.error('Delete job error:', error);
            onError(error.response?.data?.error || 'Failed to delete extraction job');
        }
    };

    return (
        <Box mb={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h5" fontWeight="bold">
                        Recent Extraction Jobs
                    </Typography>
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
                <IconButton color="primary" onClick={fetchJobs} disabled={loading}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                {loading ? (
                    <Typography>Loading...</Typography>
                ) : jobs.length === 0 ? (
                    <Paper elevation={2} sx={{ padding: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="textSecondary">
                            No extraction jobs found
                        </Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={3}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: colors.primary[400] }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.grey[100] }}>Date</TableCell>
                                    {!textbookId && <TableCell sx={{ fontWeight: 'bold', color: colors.grey[100] }}>Textbook</TableCell>}
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.grey[100] }}>Mode</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.grey[100] }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.grey[100] }}>Questions</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.grey[100] }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jobs.map((job) => (
                                    <TableRow key={job.id} hover>
                                        <TableCell>{formatDate(job.created_at)}</TableCell>
                                        {!textbookId && <TableCell>{getTextbookName(job.textbook_id)}</TableCell>}
                                        <TableCell>
                                            <Chip
                                                label={job.mode === 'crop' ? 'Crop' : 'Page Range'}
                                                size="small"
                                                color={job.mode === 'crop' ? 'primary' : 'secondary'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={job.status}
                                                size="small"
                                                color={getStatusColor(job.status)}
                                            />
                                            {job.status === 'processing' && ` ${job.progress}%`}
                                        </TableCell>
                                        <TableCell>
                                            {job.status === 'completed' ? job.question_count : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1} alignItems="center">
                                                {job.status === 'completed' && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => handleViewResults(job.id)}
                                                    >
                                                        View Results
                                                    </Button>
                                                )}
                                                {job.status === 'failed' && (
                                                    <Typography variant="caption" color="error">
                                                        {job.error_message}
                                                    </Typography>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteJob(job.id)}
                                                    title="Delete job"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={page - 1}
                            rowsPerPage={20}
                            rowsPerPageOptions={[20]}
                            onPageChange={(e, newPage) => setPage(newPage + 1)}
                        />
                    </TableContainer>
                )}
            </Collapse>
        </Box>
    );
};

export default RecentExtractionJobs;
